/**
 * LLM-powered card personalization via the copilot generate API.
 *
 * Takes heuristic-matched cards and workspace context, then asks the LLM to
 * rank them by relevance and rewrite titles/descriptions to feel personal.
 * The `prompt` field on each card is never modified — only title and description.
 *
 * Brand context (company name, industry, tone) is injected server-side via
 * `includeUserContext`, which attaches the workspace's WORKSPACE.md file
 * (containing brand context, enabled channels, etc.) to the LLM prompt.
 *
 * NOTE: If this moves to Talon in the future, replace the `generate` call with
 * a Talon claw invocation. The prompt and schema can stay the same.
 */

import { generate } from '@customerio/hydra-copilot';
import { z } from 'zod';
import { isEmptyString } from '@/utils/string';
import { sentryCaptureException } from '@/utils/sentry';
import type { PromptCard } from './prompts';
import type { WorkspaceContext } from './workspace-context';

export interface PersonalizeInput {
  cards: PromptCard[];
  workspaceContext: WorkspaceContext;
  count?: number;
}

export interface PersonalizedCard {
  /** Original card (with unmodified prompt) */
  card: PromptCard;
  /** LLM-rewritten title */
  title: string;
  /** LLM-rewritten description */
  description: string;
}

const responseSchema = z.toJSONSchema(
  z.object({
    cards: z
      .array(
        z.object({
          index: z.number().describe('Zero-based index of the card from the candidate list'),
          title: z.string().describe('Personalized title (max 60 chars)'),
          description: z.string().describe('Personalized description (max 120 chars)'),
        }),
      )
      .describe('Exactly 4 cards, ranked from most to least relevant'),
  }),
);

function buildWorkspaceSummary(context: WorkspaceContext): string {
  const lines: string[] = [];

  // Setup state
  if (context.integrationCompletedAt != null) {
    lines.push('Data source: connected');
  } else {
    lines.push('Data source: not yet connected');
  }

  lines.push(`Email sending: ${context.deliverySettings === 'normal' ? 'live' : 'test mode'}`);

  if (context.enabledActions.length > 0) {
    lines.push(`Enabled channels: ${context.enabledActions.join(', ')}`);
  }

  if (context.subscriptionCenterEnabled) {
    lines.push('Subscription center: enabled');
  }

  if (!isEmptyString(context.languageAttribute)) {
    lines.push(`Language attribute: ${context.languageAttribute}`);
  }

  if (context.objectTypesCount > 0) {
    lines.push(`Custom object types: ${context.objectTypesCount}`);
  }

  if (context.pushProviders.length > 0) {
    lines.push(`Push providers: ${context.pushProviders.join(', ')}`);
  }

  // Segment names (user-created only, sample segments excluded)
  if (context.segmentNames.length > 0) {
    const maxNames = 50;
    const displayed = context.segmentNames.slice(0, maxNames);
    const suffix =
      context.segmentNames.length > maxNames ? ` (and ${context.segmentNames.length - maxNames} more)` : '';
    lines.push(`User-created segments: ${displayed.map((n) => `"${n}"`).join(', ')}${suffix}`);
  }

  // Counts
  if (context.counts != null) {
    const c = context.counts;
    lines.push(`Campaigns: ${c.campaignsCount} (${c.campaignsArchivedCount} archived)`);
    lines.push(`Segments: ${c.segmentsCount}`);
    lines.push(`Newsletters/broadcasts: ${c.newslettersCount}`);
    lines.push(`Transactional messages: ${c.transactionalCount}`);
    lines.push(`Tracked events: ${c.eventsCount}`);
    lines.push(`Customer attributes: ${c.customerAttributesCount}`);
  }

  // Plan
  if (context.premium) {
    lines.push('Plan: premium');
  } else if (context.premiumTrial) {
    lines.push('Plan: premium trial');
  } else {
    lines.push('Plan: essentials');
  }

  return lines.join('\n');
}

function buildPrompt(input: PersonalizeInput): string {
  const count = input.count ?? 4;
  const workspaceSummary = buildWorkspaceSummary(input.workspaceContext);

  const cardList = input.cards.map((card, index) => `${index}. "${card.title}" — ${card.description}`).join('\n');

  return `You are a personalization engine for Customer.io's home page. Your job is to pick the ${count} most relevant conversation starter cards for this workspace and rewrite their titles and descriptions to feel tailored.

## Workspace State

${workspaceSummary}

## Candidate Cards

${cardList}

## Instructions

1. **Rank**: Choose the ${count} cards most relevant to this workspace right now, considering their setup state, what they've built so far, and what would help them most next.
2. **Rewrite**: For each chosen card, rewrite the title and description to feel specific to this workspace. Use the brand context from the workspace file (company name, industry, audience) to make the copy feel personal — but only if brand context is available. If no brand context exists, keep the language general but still natural.
3. **Return**: Output exactly ${count} cards in the response schema, ordered from most to least relevant.

## Rules

- Titles must be under 60 characters. Descriptions must be under 120 characters.
- Keep titles action-oriented (start with a verb when possible).
- Do not invent features or capabilities that Customer.io does not have.
- Do not mention competitor products by name.
- Do not include the company's own name in every card — use it sparingly (at most once across all ${count} cards).
- Match the brand's tone if tone context is available. Otherwise, be friendly and professional.
- The rewrite should feel like a helpful suggestion, not a sales pitch.
- If the brand context says nsfw=true, you may be slightly edgier in tone, but stay professional.`;
}

/**
 * Calls the copilot generate API to rank and personalize prompt cards.
 *
 * Returns null if the LLM call fails or returns unexpected data — the caller
 * should fall back to the heuristic-only cards.
 */
export async function personalizeCards(input: PersonalizeInput): Promise<PersonalizedCard[] | null> {
  const count = input.count ?? 4;

  if (input.cards.length === 0) return null;

  try {
    const prompt = buildPrompt(input);

    const response = await generate(prompt, responseSchema, {
      includeGlobalContext: false,
      includeUserContext: true,
    });

    if (response == null || !Array.isArray(response.cards)) {
      return null;
    }

    const result: PersonalizedCard[] = [];
    for (const item of response.cards) {
      // Validate LLM response fields at runtime — the schema guides the LLM
      // but doesn't guarantee correct types in the response.
      if (typeof item.index !== 'number' || !Number.isFinite(item.index)) continue;
      const index = item.index;

      const card = input.cards[index];
      if (card == null) continue;

      const itemTitle = typeof item.title === 'string' ? item.title : '';
      const itemDescription = typeof item.description === 'string' ? item.description : '';
      result.push({
        card,
        title: isEmptyString(itemTitle) ? card.title : itemTitle,
        description: isEmptyString(itemDescription) ? card.description : itemDescription,
      });

      if (result.length >= count) break;
    }

    return result.length > 0 ? result : null;
  } catch (error) {
    sentryCaptureException(error);
    return null;
  }
}
